import { useState, useCallback } from "react";
import {
  FileDoc,
  MathOperations,
  MarkdownLogo,
  CaretRight,
  Folder,
  FolderOpen,
} from "@phosphor-icons/react";
import { cn } from "../../../lib/cn";
import {
  getDemoTemplatesByType,
  type DemoTemplate,
  type DocType,
} from "../../../lib/demoTemplates";

const GROUPS: { type: DocType; label: string }[] = [
  { type: "latex", label: "LaTeX" },
  { type: "markdown", label: "Markdown" },
  { type: "docx", label: "Documents" },
];

export function DemoFileTree({
  activeId,
  onSelect,
}: {
  activeId: string;
  onSelect: (t: DemoTemplate) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(["latex", "markdown", "docx"]),
  );

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return (
    <div className="flex flex-col h-full overflow-y-auto py-0.5">
      {GROUPS.map(({ type, label }) => {
        const templates = getDemoTemplatesByType(type);
        const isOpen = expanded.has(type);
        return (
          <div key={type}>
            <button
              onClick={() => toggle(type)}
              className={cn(
                "group flex w-full items-center gap-1.5 py-1 text-left text-[12px]",
                "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
                "transition-colors duration-75",
              )}
              style={{ paddingLeft: 8 }}
            >
              <CaretRight
                size={10}
                weight="bold"
                className={cn(
                  "shrink-0 text-[var(--text-muted)] transition-transform duration-100",
                  isOpen && "rotate-90",
                )}
              />
              {isOpen ? (
                <FolderOpen size={14} weight="fill" className="text-[var(--color-cool-azure)] shrink-0" />
              ) : (
                <Folder size={14} weight="fill" className="text-[var(--text-muted)] shrink-0" />
              )}
              <span className="flex-1 truncate font-medium">{label}</span>
            </button>
            {isOpen &&
              templates.map((t) => {
                const ext =
                  t.doc_type === "latex"
                    ? ".tex"
                    : t.doc_type === "markdown"
                      ? ".md"
                      : ".docx";
                const filename = t.label.toLowerCase().replace(/\s+/g, "-") + ext;
                const Icon =
                  t.doc_type === "latex"
                    ? MathOperations
                    : t.doc_type === "markdown"
                      ? MarkdownLogo
                      : FileDoc;
                const isActive = t.id === activeId;
                return (
                  <button
                    key={t.id}
                    onClick={() => onSelect(t)}
                    className={cn(
                      "group flex w-full items-center gap-2 py-1.5 text-left text-[12px]",
                      "transition-colors duration-75",
                      isActive
                        ? "bg-[var(--color-lever-orange)]/8 text-[var(--text-primary)]"
                        : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
                    )}
                    style={{ paddingLeft: 22 }}
                  >
                    <Icon
                      size={13}
                      weight={isActive ? "fill" : "regular"}
                      className={cn(
                        "shrink-0",
                        isActive
                          ? "text-[var(--color-lever-orange)]"
                          : "text-[var(--text-muted)]",
                      )}
                    />
                    <span className="truncate">{filename}</span>
                  </button>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}
